/**
 * OrderForm — public page, no sign-in (Joe, 5 Nov 2026).
 * /order/:token — someone PLD sent the link to picks products and
 * quantities and says how it's getting to them. It lands in Orders → Email
 * inbox for the team to check and confirm.
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const GREEN = '#22391f';
const YELLOW = '#FFDC06';

const inputStyle = {
  padding: '10px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 15, background: '#fff',
  width: '100%', boxSizing: 'border-box', color: '#111', outline: 'none',
};
const Input = (props) => <input {...props} style={{ ...inputStyle, ...props.style }} />;

function Field({ label, required, hint, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: '#2d2d2d' }}>{label}{required && <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>}</span>
      {children}
      {hint && <span style={{ fontSize: 12, color: '#6b7280' }}>{hint}</span>}
    </label>
  );
}

function Section({ n, title, children }) {
  return (
    <section style={{ marginTop: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <span style={{ width: 28, height: 28, borderRadius: 99, background: GREEN, color: '#fff', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{n}</span>
        <h3 style={{ margin: 0, fontSize: 16, color: GREEN }}>{title}</h3>
      </div>
      {children}
    </section>
  );
}

const money = (n) => `£${Number(n).toFixed(2)}`;
const longDate = (d) => new Date(d).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

export default function OrderForm() {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [linkError, setLinkError] = useState('');
  const [qty, setQty] = useState({});
  const [d, setD] = useState({ name: '', company: '', email: '', phone: '', method: '', line1: '', line2: '', city: '', postcode: '', wanted_by: '', notes: '', website: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`/api/public/order-form/${token}`)
      .then(async (r) => { const j = await r.json().catch(() => ({})); if (!r.ok) throw new Error(j.error || j.message || "This order form isn't open"); return j; })
      .then((j) => { setForm(j); setD((x) => ({ ...x, method: j.allow_delivery ? 'deliver' : 'collect' })); })
      .catch((e) => setLinkError(e.message));
  }, [token]);

  const set = (k) => (e) => setD((x) => ({ ...x, [k]: e.target.value }));
  const chosen = (form?.products || []).filter((p) => Number(qty[p.id]) > 0);
  const priced = form?.products?.some((p) => p.price != null);
  const total = chosen.reduce((s, p) => s + (p.price || 0) * Number(qty[p.id]), 0);

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (!chosen.length) { setError('Put a quantity against at least one product.'); return; }
    setSaving(true);
    try {
      const r = await fetch(`/api/public/order-form/${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: d.name, company: d.company, email: d.email, phone: d.phone, method: d.method, wanted_by: d.wanted_by, notes: d.notes, website: d.website,
          address: { line1: d.line1, line2: d.line2, city: d.city, postcode: d.postcode },
          lines: chosen.map((p) => ({ stock_item_id: p.id, quantity: Number(qty[p.id]) })),
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || j.message || "That didn't go through — please try again");
      setDone(true);
      window.scrollTo(0, 0);
    } catch (ex) { setError(ex.message); } finally { setSaving(false); }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ background: GREEN, padding: '0 16px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '18px 0', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 42, height: 42, borderRadius: '50%', background: YELLOW, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 15, color: GREEN }}>PL</div>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>Pleasant Land Distillery</div>
            <div style={{ color: 'rgba(255,255,255,.65)', fontSize: 12 }}>Order form</div>
          </div>
        </div>
      </div>
      <div style={{ height: 4, background: YELLOW }} />

      <div style={{ maxWidth: 760, margin: '28px auto', padding: '0 16px 48px' }}>
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.1)', padding: '26px 24px 28px' }}>
          {linkError ? (
            <div style={{ textAlign: 'center', padding: '30px 0' }}>
              <h2 style={{ color: GREEN, margin: '0 0 10px' }}>This form isn't open</h2>
              <p style={{ color: '#374151', margin: 0 }}>{linkError}</p>
            </div>
          ) : !form ? (
            <p style={{ color: '#6b7280', textAlign: 'center' }}>Loading…</p>
          ) : done ? (
            <div style={{ textAlign: 'center', padding: '30px 0' }}>
              <div style={{ width: 60, height: 60, borderRadius: 99, background: '#dcfce7', color: '#166534', fontSize: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>✓</div>
              <h2 style={{ color: GREEN, margin: '0 0 10px' }}>Thank you — we've got your order</h2>
              <p style={{ color: '#374151', margin: 0, lineHeight: 1.6 }}>We'll check it and be in touch at {d.email} to confirm it and arrange payment.</p>
            </div>
          ) : (
            <form onSubmit={submit}>
              <h1 style={{ margin: '0 0 8px', fontSize: 22, color: GREEN }}>{form.title}</h1>
              {form.intro && <p style={{ margin: 0, color: '#4b5563', fontSize: 14.5, lineHeight: 1.6, whiteSpace: 'pre-line' }}>{form.intro}</p>}
              {form.closes_on && <p style={{ margin: '10px 0 0', fontSize: 13.5, color: '#92400e' }}>Orders by {longDate(form.closes_on)}, please.</p>}

              <Section n={1} title="What would you like?">
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8 }}>
                  {form.products.map((p, i) => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderTop: i ? '1px solid #f0f0f0' : 'none', background: Number(qty[p.id]) > 0 ? '#f0fdf4' : '#fff' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14.5, fontWeight: 600, color: '#111' }}>{p.name}</div>
                        {p.price != null && <div style={{ fontSize: 13, color: '#6b7280' }}>{money(p.price)} each{form.prices_include_vat ? '' : ' + VAT'}</div>}
                      </div>
                      <input type="number" min="0" step="1" inputMode="numeric" aria-label={`How many ${p.name}`} value={qty[p.id] ?? ''} placeholder="0"
                        onChange={(e) => setQty((q) => ({ ...q, [p.id]: e.target.value }))}
                        style={{ ...inputStyle, width: 76, textAlign: 'center' }} />
                    </div>
                  ))}
                </div>
                {chosen.length > 0 && (
                  <div style={{ marginTop: 10, fontSize: 14, color: GREEN, fontWeight: 600 }}>
                    {chosen.reduce((s, p) => s + Number(qty[p.id]), 0)} item{chosen.reduce((s, p) => s + Number(qty[p.id]), 0) === 1 ? "" : "s"}{priced ? ` · ${money(total)}${form.prices_include_vat ? '' : ' + VAT'}` : ''}
                  </div>
                )}
              </Section>

              <Section n={2} title="Your details">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                  <Field label="Your name" required><Input value={d.name} onChange={set('name')} required autoComplete="name" /></Field>
                  <Field label="Company" hint="If it's for a business"><Input value={d.company} onChange={set('company')} autoComplete="organization" /></Field>
                  <Field label="Email" required><Input type="email" value={d.email} onChange={set('email')} required autoComplete="email" /></Field>
                  <Field label="Phone"><Input type="tel" value={d.phone} onChange={set('phone')} autoComplete="tel" /></Field>
                </div>
                {/* Left empty by people; bots fill it in. */}
                <input type="text" tabIndex={-1} autoComplete="off" value={d.website} onChange={set('website')} aria-hidden="true" style={{ position: 'absolute', left: -9999, width: 1, height: 1 }} />
              </Section>

              <Section n={3} title="Getting it to you">
                {form.allow_collection && form.allow_delivery && (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                    {[['deliver', 'Deliver it'], ['collect', "I'll collect it from the distillery"]].map(([k, label]) => (
                      <button key={k} type="button" onClick={() => setD((x) => ({ ...x, method: k }))}
                        style={{ flex: '1 1 200px', padding: '10px 12px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', border: `1.5px solid ${d.method === k ? GREEN : '#d1d5db'}`, background: d.method === k ? GREEN : '#fff', color: d.method === k ? '#fff' : '#374151' }}>
                        {label}
                      </button>
                    ))}
                  </div>
                )}
                {d.method === 'deliver' && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
                    <Field label="Address" required><Input value={d.line1} onChange={set('line1')} required autoComplete="address-line1" /></Field>
                    <Field label="Address line 2"><Input value={d.line2} onChange={set('line2')} autoComplete="address-line2" /></Field>
                    <Field label="Town"><Input value={d.city} onChange={set('city')} autoComplete="address-level2" /></Field>
                    <Field label="Postcode" required><Input value={d.postcode} onChange={set('postcode')} required autoComplete="postal-code" /></Field>
                  </div>
                )}
                {d.method === 'collect' && <p style={{ margin: '0 0 12px', fontSize: 13.5, color: '#4b5563' }}>We'll let you know when it's ready to collect.</p>}
                <Field label={d.method === 'collect' ? 'When would you like to collect it?' : 'When do you need it by?'}>
                  <Input type="date" value={d.wanted_by} onChange={set('wanted_by')} min={new Date().toISOString().slice(0, 10)} style={{ maxWidth: 220 }} />
                </Field>
              </Section>

              <Section n={4} title="Anything else?">
                <textarea value={d.notes} onChange={set('notes')} rows={3} placeholder="Gift messages, delivery instructions…" style={{ ...inputStyle, resize: 'vertical' }} />
              </Section>

              {error && <div style={{ marginTop: 18, padding: '12px 14px', background: '#fee2e2', borderRadius: 8, color: '#991b1b', fontSize: 14 }}>{error}</div>}
              <button type="submit" disabled={saving}
                style={{ marginTop: 22, width: '100%', padding: '14px 0', borderRadius: 8, border: 'none', cursor: saving ? 'default' : 'pointer', background: saving ? '#6b7280' : GREEN, color: '#fff', fontSize: 16, fontWeight: 700 }}>
                {saving ? 'Sending…' : 'Send my order'}
              </button>
              <p style={{ margin: '10px 0 0', fontSize: 12.5, color: '#6b7280', textAlign: 'center' }}>Nothing is charged now — we'll confirm your order and arrange payment with you.</p>
            </form>
          )}
        </div>
        <p style={{ textAlign: 'center', marginTop: 18, fontSize: 12, color: '#9ca3af' }}>© {new Date().getFullYear()} Pleasant Land Distillery Ltd</p>
      </div>
    </div>
  );
}
